import type { Board } from "../board";
import { DamageType } from "../damageType";
import type { Direction } from "../direction";
import type { PointsToDirection } from "../interfaces";
import type { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import type { Thing } from "../thing";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import type { XY } from "../xy";
import type { BigSquid } from "./bigSquid";
import { BigSquidTentacleCorpse } from "./bigSquidTentacleCorpse";

export enum BigSquidTentacleState {
	EXTENDED = "EXTENDED",
	RETRACTED = "RETRACTED",
}

export class BigSquidTentacle extends Mob implements PointsToDirection {
	static key = "BigSquidTentacle";
	private direction: Direction | null = null;
	private state: BigSquidTentacleState = BigSquidTentacleState.EXTENDED;
	head!: BigSquid;

	constructor(xy: XY) {
		super(xy);
		this.resistsPermanentFreeze = true;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.damageMultipliers.set(DamageType.FIRE, 0.4);
	}

	override getBloodParticleType(): ParticleType {
		return ParticleType.BLOOD;
	}

	override countsAsKill(): boolean {
		return false;
	}

	processMobAction(_board: Board): void {
		// No-op — head controls movement
	}

	getDirection(): Direction | null {
		return this.direction;
	}

	setDirection(direction: Direction): void {
		this.direction = direction;
	}

	getState(): BigSquidTentacleState {
		return this.state;
	}

	setState(state: BigSquidTentacleState): void {
		this.state = state;
	}

	setHead(head: BigSquid): void {
		this.head = head;
	}

	generateCorpse(): PhysicalThing | null {
		return new BigSquidTentacleCorpse(this.xy);
	}

	protected onZeroHp(source: Thing | null, type: DamageType | null): void {
		super.onZeroHp(source, type);
		if (this.head && this.head.active) {
			this.head.notifyKilledTentacle(this);
		}
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			direction: this.direction,
			state: this.state,
			headXY: this.head ? { x: this.head.xy.x, y: this.head.xy.y } : null,
		};
	}

	static deserialize(data: any): BigSquidTentacle {
		const t = new BigSquidTentacle(deserializeXY(data.xy));
		t.restoreMobFields(data);
		t.direction = data.direction ?? null;
		t.state = data.state ?? BigSquidTentacleState.EXTENDED;
		// head link restored via BigSquid.postDeserialize
		return t;
	}
}
